import type {
  CreateServiceAccountRequest,
  DeleteServiceAccountRequest,
  RotateServiceAccountSecretRequest,
  UpdateServiceAccountRequest,
} from '@compass/client/types/service_account';
import { serviceAccountService } from '@services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '@/services/query.config';

export const useServiceAccounts = (includeDisabled = false) => {
  return useQuery({
    queryKey: queryKeys.serviceAccounts.list(includeDisabled),
    queryFn: () => serviceAccountService.listServiceAccounts({ include_disabled: includeDisabled }),
    staleTime: 30_000,
  });
};

export const useCreateServiceAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateServiceAccountRequest) =>
      serviceAccountService.createServiceAccount(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceAccounts.all });
    },
  });
};

export const useRotateServiceAccountSecret = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: RotateServiceAccountSecretRequest) =>
      serviceAccountService.rotateServiceAccountSecret(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceAccounts.all });
    },
  });
};

export const useUpdateServiceAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateServiceAccountRequest) =>
      serviceAccountService.updateServiceAccount(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceAccounts.all });
    },
  });
};

export const useDeleteServiceAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DeleteServiceAccountRequest) =>
      serviceAccountService.deleteServiceAccount(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceAccounts.all });
      toast.success('Service account deleted');
    },
  });
};
